/**
 * useWorkspaceMembers — the active workspace's members, as Central lists them.
 *
 * The same browser-direct Central call the Settings → Members tab makes
 * (services/membershipApi.listMembers, Bearer only). Used to pick training
 * assignees; the server re-checks every assignee against Central before it
 * writes anything.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { WorkspaceMember } from '@levelup/central-auth';
import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';
import { selectBootCurrentWorkspace } from '../store/slices/bootContextSlice';
import { listMembers } from '../services/membershipApi';

export interface WorkspaceMembersState {
  workspaceId: string | null;
  members: WorkspaceMember[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useWorkspaceMembers(): WorkspaceMembersState {
  const token = useSelector(selectCentralAccessToken);
  const workspace = useSelector(selectBootCurrentWorkspace);
  const workspaceId = workspace?.id ?? null;
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId || !token) {
      setIsLoading(false);
      setError('Your workspace could not be determined.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const list = await listMembers(token, workspaceId);
      setMembers(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Members could not be loaded.');
      setMembers([]);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  return { workspaceId, members, isLoading, error, refetch: () => void load() };
}
